import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    TextField,
    CircularProgress,
    makeStyles
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles(theme => ({
    codeContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(2),
    },
    digitField: {
        width: 50,
        height: 45,
        borderRadius: 8,
        border: '2px solid #4B4B4B',
        margin: theme.spacing(0.5),
        '& .MuiInputBase-input': {
            textAlign: 'center',
            fontSize: '2.5rem',
            fontWeight: 'bold',
            padding: theme.spacing(1),
        },
    },
    error: {
        marginTop: theme.spacing(1),
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
    },
    resendText: {
        cursor: 'pointer',
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        marginTop: theme.spacing(1),
        display: 'inline-block'
    },
    resendContainer: {
        marginTop: theme.spacing(1),
        textAlign: 'center'
    }
}));

const VerificationCodeDialog = ({ open, onClose, onVerify, onResend, email, loading, error }) => {
    const classes = useStyles();
    const [digits, setDigits] = useState(Array(6).fill(''));
    const [showError, setShowError] = useState(false);

    // State for resend link
    const [resendStatus, setResendStatus] = useState('idle'); // 'idle', 'sending', 'sent'
    const [resendCountdown, setResendCountdown] = useState(20);

    // Create individual refs for each input field
    const digitRefs = [
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null)
    ];

    // Focus the first input when dialog opens and clear previous state
    useEffect(() => {
        if (open) {
            // Clear digits and focus first input when dialog opens
            setDigits(Array(6).fill(''));
            setResendStatus('idle');
            setResendCountdown(20);
            setShowError(false); // Clear error display
            if (digitRefs[0].current) {
                digitRefs[0].current.focus();
            }
        }
    }, [open]);

    // Show error after a brief delay to allow for state clearing
    useEffect(() => {
        if (error && open) {
            const timer = setTimeout(() => {
                setShowError(true);
            }, 100);
            return () => clearTimeout(timer);
        } else {
            setShowError(false);
        }
    }, [error, open]);

    // Handle countdown for resend text
    useEffect(() => {
        let timer;
        if (resendStatus === 'sent') {
            timer = setInterval(() => {
                setResendCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        setResendStatus('idle');
                        return 20;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendStatus]);

    const handleChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newDigits = [...digits];
            newDigits[index] = value;
            setDigits(newDigits);
            if (value && index < 5) {
                digitRefs[index + 1].current.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('Text');
        const pasteDigits = paste.replace(/\D/g, '').split('').slice(0, 6);
        if (pasteDigits.length > 0) {
            const newDigits = Array(6).fill('');
            pasteDigits.forEach((d, index) => {
                newDigits[index] = d;
            });
            setDigits(newDigits);
            const next = pasteDigits.length < 6 ? pasteDigits.length : 5;
            if (digitRefs[next].current) {
                digitRefs[next].current.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            digitRefs[index - 1].current.focus();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const code = digits.join('');
        if (code.length === 6) {
            onVerify(code);
        }
    };

    const handleResendClick = async () => {
        if (resendStatus === 'idle') {
            setResendStatus('sending');
            // Clear previous digits and errors when resending
            setDigits(Array(6).fill(''));
            setShowError(false);
            try {
                await onResend();
                setResendStatus('sent');
                setResendCountdown(20);
            } catch (err) {
                setResendStatus('idle');
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (loading && reason === 'backdropClick') return;
                onClose();
            }}
        >
            <DialogTitle>Email Verification</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Typography variant="body1" align="center">
                        A verification code has been sent to {email}.
                    </Typography>
                    {showError && error && (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Alert severity="error" className={classes.error}>
                                {error}
                            </Alert>
                        </div>
                    )}
                    <Grid container justifyContent="center" className={classes.codeContainer}>
                        {digits.map((digit, index) => (
                            <TextField
                                key={index}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                variant="outlined"
                                inputProps={{ maxLength: 1 }}
                                className={classes.digitField}
                                disabled={loading}
                                inputRef={digitRefs[index]}
                            />
                        ))}
                    </Grid>
                    <Typography variant="caption" display="block" align="center" color="textSecondary">
                        The code will expire in 15 minutes.
                    </Typography>
                    <div className={classes.resendContainer}>
                        {resendStatus === 'sending' ? (
                            <CircularProgress size={16} />
                        ) : (
                            <Typography
                                variant="body2"
                                className={classes.resendText}
                                onClick={handleResendClick}
                            >
                                {resendStatus === 'sent'
                                    ? `Sent (${resendCountdown})`
                                    : 'Resend Code'}
                            </Typography>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button disabled={loading} onClick={onClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={digits.some(d => d === '') || loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Verify'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default VerificationCodeDialog;
